import { Injectable, inject } from '@angular/core';
import { ToastService } from '@shared/services/toast.service';
import {
  CV_SCREENING_DOCUMENT_OUTCOME,
  type CvScreeningDocumentUpdatedData,
  type CvScreeningJobReadyData,
} from '@tribuu-shared-notifications';
import { CvCandidatesStateService } from './cv-candidates-state.service';
import { CvScreeningStateService } from './cv-screening-state.service';

/**
 * How long the `document-updated` pushes of the open vacancy are gathered before one refetch. The
 * window is fixed: it opens with the first push and is never pushed back by later ones, so a long
 * burst cannot postpone the refetch indefinitely (D-CR-6).
 */
export const CV_LIVE_REFRESH_WINDOW_MS = 1500;

/** The notice shown when none of the vacancy's CVs is still in process (D-CR-6). */
const LIST_READY_NOTICE = {
  title: 'Listado disponible',
  body: 'Ya terminaron de procesarse los CVs de esta vacante.',
  type: 'info',
} as const;

/** The pushes gathered for the open vacancy while its window is open. */
interface PendingWindow {
  timer: ReturnType<typeof setTimeout>;
  sawCompleted: boolean;
}

/**
 * Turns the CV screening pushes into authoritative refetches of the vacancy the «Preliminares» panel
 * is showing (SDD 2026-10-01-cv-screening-candidate-review, D-CR-6). The push is a trigger, never a
 * source of data: its payload only says which vacancy moved, and what shows on screen always comes
 * from `GET …/documents` and `GET …/candidates`. A push of another vacancy, or with no panel
 * mounted, does nothing. There is no polling: the only timer is the window a received push opens.
 */
@Injectable({ providedIn: 'root' })
export class CvScreeningLiveRefreshService {
  private readonly progress = inject(CvScreeningStateService);
  private readonly candidates = inject(CvCandidatesStateService);
  private readonly toast = inject(ToastService);

  /** The vacancy the mounted panel is showing; `null` when no panel is mounted. */
  private watchedJobId: string | null = null;
  private pending: PendingWindow | null = null;

  watch(jobId: string): void {
    if (this.watchedJobId !== jobId) this.cancelWindow();
    this.watchedJobId = jobId;
  }

  /** The panel stopped showing the vacancy: its pushes, and a window still open, mean nothing now. */
  unwatch(jobId: string): void {
    if (this.watchedJobId !== jobId) return;
    this.cancelWindow();
    this.watchedJobId = null;
  }

  /** One CV of a vacancy reached a final status (one push per CV). */
  handleDocumentUpdated(data: CvScreeningDocumentUpdatedData): void {
    if (data.jobUid !== this.watchedJobId) return;
    const completed = data.status === CV_SCREENING_DOCUMENT_OUTCOME.COMPLETED;
    if (this.pending) {
      this.pending.sawCompleted ||= completed;
      return;
    }
    this.pending = {
      sawCompleted: completed,
      timer: setTimeout(() => this.closeWindow(), CV_LIVE_REFRESH_WINDOW_MS),
    };
  }

  /**
   * None of the vacancy's CVs is still in process: refetch both listings at once (the window it
   * ends would only repeat them) and say the list is available.
   */
  handleJobReady(data: CvScreeningJobReadyData): void {
    if (data.jobUid !== this.watchedJobId) return;
    this.cancelWindow();
    this.progress.refresh();
    this.candidates.refresh();
    this.toast.show(LIST_READY_NOTICE);
  }

  /** The window ran out: one progress refetch, and the candidates only if a CV completed in it. */
  private closeWindow(): void {
    const sawCompleted = this.pending?.sawCompleted ?? false;
    this.pending = null;
    this.progress.refresh();
    if (sawCompleted) this.candidates.refresh();
  }

  private cancelWindow(): void {
    if (this.pending) clearTimeout(this.pending.timer);
    this.pending = null;
  }
}
